// firebase.js — Firebase 초기화 및 헬퍼
import { initializeApp } from "firebase/app";
import {
  getAuth, GoogleAuthProvider, signInWithPopup, signInAnonymously,
  signOut, onAuthStateChanged,
} from "firebase/auth";
import {
  getFirestore, doc, setDoc, getDoc, getDocs, collection, onSnapshot,
} from "firebase/firestore";

const firebaseConfig = {
  apiKey: "AIzaSyDyH2bj9-mTk_e01kqFFeC5nXKMU0nc0ZE",
  authDomain: "inventory-manager-de0e7.firebaseapp.com",
  databaseURL: "https://inventory-manager-de0e7-default-rtdb.asia-southeast1.firebasedatabase.app",
  projectId: "inventory-manager-de0e7",
  storageBucket: "inventory-manager-de0e7.firebasestorage.app",
  messagingSenderId: "280763795837",
  appId: "1:280763795837:web:fc86c32e8d5d41c80f6414",
};

const app = initializeApp(firebaseConfig);
const auth = getAuth(app);
const db = getFirestore(app);
const googleProvider = new GoogleAuthProvider();
export { app };

// ===== 연동 설정 (settings/naver — 네이버 API 허브 키, 관리자 전용) =====
export const saveNaverSettings = (clientId, clientSecret) =>
  setDoc(doc(db, "settings", "naver"), { clientId: clientId.trim(), clientSecret: clientSecret.trim(), updatedAt: new Date().toISOString() });
export const getNaverSettings = async () => {
  const snap = await getDoc(doc(db, "settings", "naver"));
  return snap.exists() ? snap.data() : null;
};

// ===== 주간 경영관리 리포트 발송 설정 (settings/reportConfig — 관리자 전용) =====
// recipients: 수신자 이메일 배열 · gmailUser/gmailAppPassword: 발송용 Gmail 계정과 앱 비밀번호
// (일반 Gmail 비밀번호가 아니라 구글 계정 "앱 비밀번호" 16자리를 사용해야 함)
// enabled: false로 두면 매주 자동발송은 건너뛰고 "테스트 발송" 버튼만 동작.
export const saveReportConfig = (cfg) =>
  setDoc(doc(db, "settings", "reportConfig"), {
    recipients: (cfg.recipients || []).map((s) => s.trim()).filter(Boolean),
    gmailUser: (cfg.gmailUser || "").trim(),
    gmailAppPassword: (cfg.gmailAppPassword || "").trim(),
    enabled: cfg.enabled !== false,
    updatedAt: new Date().toISOString(),
  }, { merge: true });
export const getReportConfig = async () => {
  const snap = await getDoc(doc(db, "settings", "reportConfig"));
  return snap.exists() ? snap.data() : null;
};

// ===== 인증 헬퍼 =====
export const loginWithGoogle = () => signInWithPopup(auth, googleProvider);
export const loginAnonymously = () => signInAnonymously(auth);
export const logout = () => signOut(auth);
export const watchAuth = (cb) => onAuthStateChanged(auth, cb);

// ===== Firestore 데이터 =====
// 팀 전체가 같은 데이터를 공유하도록 공용 문서 1개를 사용.
// (사용자별 분리가 필요하면 docId를 uid로 바꾸면 됨)
const SHARED_DOC = doc(db, "shared", "inventory");

// 주의: history / salesHistory / valueHistory는 여기 포함하지 않는다.
// 이 셋은 아래 "이력 데이터" 섹션의 날짜별 서브컬렉션에 따로 저장한다.
// (예전에는 이 문서 하나에 모든 날짜의 이력을 다 넣었는데, 실시간 리스너가
//  갱신될 때마다 누적된 이력 전체를 매번 다시 전송해서 Firebase 대역폭
//  사용량이 계속 늘어나는 원인이 됐다. 라이브 문서는 가볍게 유지한다.)
export const saveData = (data) => {
  const { history, salesHistory, valueHistory, ...light } = data || {};
  return setDoc(SHARED_DOC, { ...light, updatedAt: Date.now() }, { merge: true });
};

export const loadData = async () => {
  const snap = await getDoc(SHARED_DOC);
  return snap.exists() ? snap.data() : null;
};

// 실시간 구독: 다른 사람이 저장하면 자동으로 콜백 호출 (가벼운 필드만 전송됨)
export const watchData = (cb) =>
  onSnapshot(SHARED_DOC, (snap) => {
    if (snap.exists()) cb(snap.data());
  });

// ===== 이력 데이터 (history / salesHistory / valueHistory) =====
// 날짜별로 별도 문서에 저장한다 (shared/inventory/{kind}/{날짜}).
// 실시간 리스너를 걸지 않고, 로그인 시 1회만 전체를 불러온다 — 그래서
// 이력이 아무리 쌓여도 매번 재전송되지 않는다.
const historyCol = (kind) => collection(db, "shared", "inventory", kind);

// 날짜 하나의 이력만 저장 (재고 스냅샷 기록 / 일별 판매 업로드 시 호출)
export const saveHistoryDay = (kind, dateKey, value) =>
  setDoc(doc(historyCol(kind), dateKey), { value, updatedAt: Date.now() }, { merge: true });

// 특정 종류(history|salesHistory|valueHistory)의 전체 날짜를 한 번에 불러옴
// (예전 마이그레이션으로 만들어진 문서는 value 대신 products 필드를 쓰므로 둘 다 지원)
export const loadAllHistory = async (kind) => {
  const snap = await getDocs(historyCol(kind));
  const out = {};
  snap.forEach((d) => {
    const data = d.data();
    out[d.id] = data.value !== undefined ? data.value : data.products;
  });
  return out;
};

export { auth, db };
